import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Phone, Users, Stethoscope, Building2 } from 'lucide-react';
import CompanyBadge from './CompanyBadge';
import { siteStats } from '../data/siteStats';

export const Hero = ({ onGetConsultation, onBookAppointment }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section className="relative">
      {/* Video block - 92% of viewport */}
      <div className="relative h-[92vh] flex items-center overflow-hidden">
        {/* Decorative background. The poster paints immediately so the hero
            is not blank while the clip downloads, and keeps the video off the
            critical path for Largest Contentful Paint. */}
        <video
          src="/videos/doctor_patient_video.mp4"
          poster="/videos/doctor_patient_poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/70 to-transparent pointer-events-none"
          aria-hidden
        />

        <div className="relative z-10 container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div
              className={`space-y-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
            >




              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-brand-navy leading-tight cursor-default">
                <span className="inline-block hover:scale-105 hover:text-brand-blue transition-all duration-300 origin-left">
                  World-Class Medical
                </span>
                <br />
                <span className="inline-block hover:scale-105 hover:text-brand-blue transition-all duration-300 origin-left">
                  Treatment in{' '}
                </span>
                <span className="relative inline-block text-transparent bg-clip-text bg-gradient-to-r from-brand-blue to-brand-teal hover:from-brand-saffron hover:to-red-500 transition-all duration-500 hover:scale-110 cursor-pointer filter hover:drop-shadow-lg">
                  India
                  <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-brand-blue to-brand-teal scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
                </span>
              </h1>

              <p className="text-lg lg:text-xl text-gray-600 leading-relaxed max-w-xl hover:text-gray-900 transition-colors duration-300">
                Access affordable, high-quality healthcare from internationally accredited hospitals.
                Save 60-80% on medical costs while receiving world-class treatment.
              </p>

              <div className="flex flex-wrap gap-3 pt-4">
                <Button
                  size="lg"
                  onClick={onGetConsultation}
                  className="group relative overflow-hidden bg-brand-saffron hover:bg-brand-saffron text-brand-navy font-bold px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-orange-500/30 transition-all duration-300 hover:-translate-y-1 active:scale-95"
                >
                  <span className="relative z-10 flex items-center">
                    Get Free Consultation
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                  {/* Shine effect */}
                  <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/30 to-transparent z-0" />
                </Button>

                <Button
                  size="lg"
                  variant="outline"
                  onClick={onBookAppointment}
                  className="group relative overflow-hidden border-2 border-brand-teal text-brand-teal bg-white/90 px-8 py-6 text-lg rounded-xl font-bold transition-all duration-300 hover:text-white hover:border-brand-teal hover:-translate-y-1 hover:shadow-teal-500/20 shadow-sm"
                >
                  <span className="relative z-10 flex items-center">
                    <Phone className="mr-2 h-5 w-5 transition-transform duration-300 group-hover:rotate-12" />
                    Book Appointment
                  </span>
                  {/* Fill effect */}
                  <div className="absolute inset-0 bg-brand-teal translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out z-0" />
                </Button>
              </div>
              <CompanyBadge className="w-fit" />
            </div>

            <div
              className={`hidden lg:flex justify-end transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                }`}
            />
          </div>
        </div>
      </div>

      {/* Card strip - overlaps bottom of video by ~half */}
      <div className="relative z-20 -mt-[10vh] bg-gradient-to-b from-white to-gray-50/95 min-h-[20vh] flex items-center border-t border-gray-200/80 shadow-[0_-8px_32px_-8px_rgba(0,0,0,0.12)] rounded-t-2xl mx-4 lg:mx-6 pb-2">
        <div className="container mx-auto px-6 py-8 lg:py-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-0">
            {/* 24/7 Support */}
            <div className="group relative flex items-center gap-4 p-4 lg:p-5 rounded-2xl bg-white/80 backdrop-blur-sm border border-gray-100 shadow-sm transition-all duration-300 cursor-pointer lg:rounded-r-none lg:border-r-0 first:lg:rounded-l-2xl first:lg:rounded-r-none hover:z-50 hover:scale-110 hover:shadow-2xl hover:bg-white hover:border-brand-blue hover:border hover:rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-blue to-brand-teal flex items-center justify-center shrink-0 shadow-lg shadow-brand-blue/20 group-hover:scale-105 group-hover:from-white group-hover:to-white group-hover:ring-2 group-hover:ring-brand-blue/30 transition-all duration-300">
                <Phone className="h-6 w-6 text-white group-hover:text-brand-navy transition-colors duration-300" />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest group-hover:text-brand-blue transition-colors">24/7 Support</div>
                <div className="text-base font-bold text-gray-900 mt-0.5">Always Available</div>
              </div>
            </div>

            {/* Happy Patients */}
            <div className="group relative flex items-center gap-4 p-4 lg:p-5 rounded-2xl bg-white/80 backdrop-blur-sm border border-gray-100 shadow-sm transition-all duration-300 cursor-pointer lg:rounded-none lg:border-r-0 hover:z-50 hover:scale-110 hover:shadow-2xl hover:bg-white hover:border-brand-blue hover:border hover:rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-brand-blue/10 flex items-center justify-center shrink-0 border border-brand-blue/20 group-hover:bg-brand-blue transition-colors duration-300">
                <Users className="h-6 w-6 text-brand-blue group-hover:text-white transition-colors duration-300" />
              </div>
              <div className="min-w-0">
                <div className="text-2xl font-bold text-brand-blue">{siteStats.patients.value}</div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider group-hover:text-brand-blue transition-colors">Happy Patients</div>
              </div>
            </div>

            {/* Expert Doctors */}
            <div className="group relative flex items-center gap-4 p-4 lg:p-5 rounded-2xl bg-white/80 backdrop-blur-sm border border-gray-100 shadow-sm transition-all duration-300 cursor-pointer lg:rounded-none lg:border-r-0 hover:z-50 hover:scale-110 hover:shadow-2xl hover:bg-white hover:border-brand-teal hover:border hover:rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-brand-teal/10 flex items-center justify-center shrink-0 border border-brand-teal/20 group-hover:bg-brand-teal transition-colors duration-300">
                <Stethoscope className="h-6 w-6 text-brand-teal group-hover:text-white transition-colors duration-300" />
              </div>
              <div className="min-w-0">
                <div className="text-2xl font-bold text-brand-teal">{siteStats.doctors.value}</div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider group-hover:text-brand-teal transition-colors">Expert Doctors</div>
              </div>
            </div>

            {/* Partner Hospitals */}
            <div className="group relative flex items-center gap-4 p-4 lg:p-5 rounded-2xl bg-white/80 backdrop-blur-sm border border-gray-100 shadow-sm transition-all duration-300 cursor-pointer lg:rounded-l-none last:lg:rounded-r-2xl last:lg:rounded-l-none hover:z-50 hover:scale-110 hover:shadow-2xl hover:bg-white hover:border-brand-green hover:border hover:rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-brand-green/10 flex items-center justify-center shrink-0 border border-brand-green/20 group-hover:bg-brand-green transition-colors duration-300">
                <Building2 className="h-6 w-6 text-brand-green group-hover:text-white transition-colors duration-300" />
              </div>
              <div className="min-w-0">
                <div className="text-2xl font-bold text-brand-green">{siteStats.hospitals.value}</div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider group-hover:text-brand-green transition-colors">Partner Hospitals</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};